import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("home");
  const [selectedTopic, setSelectedTopic] = useState(null);

  const chatConfig = useMemo(
    () => ({
      title: "Chat Support",
      subtitle: "How can we help you today?",
      homeOptions: [
        {
          id: "customer",
          label: "Customer Support",
          icon: "bi bi-headset",
          description: "Billing, bookings, service help, and general support.",
          type: "contact",
          data: {
            title: "Customer Support",
            email: "customer_support@digilaboursolutions.com",
            phone: "+91-8080808080",
            helpLink: "/contact_us",
          },
        },
        {
          id: "jobs",
          label: "Looking for Job",
          icon: "bi bi-briefcase-fill",
          description: "Job registration, labour onboarding, and hiring help.",
          type: "contact",
          data: {
            title: "Looking for Job",
            email: "hr@digilaboursolutions.com",
            phone: "+91-9090909080",
            helpLink: "/contact_us",
          },
        },
        {
          id: "faq",
          label: "Quick Questions",
          icon: "bi bi-patch-question-fill",
          description: "Predefined answers for common user questions.",
          type: "faq",
          data: {
            title: "Quick Questions",
            questions: [
              {
                question: "How do I book a worker?",
                answer:
                  "Go to the relevant service page, choose your requirement, and submit your request through the platform.",
              },
              {
                question: "How do I contact support?",
                answer:
                  "You can use the Contact page, call support, or send an email from the options shown here.",
              },
              {
                question: "How do I register for a job?",
                answer:
                  "Use the labour registration or labour login section and complete your profile with required details.",
              },
            ],
          },
        },
      ],
    }),
    []
  );

  const selectedItem = chatConfig.homeOptions.find(
    (item) => item.id === selectedTopic
  );

  const toggleChatbot = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!next) {
        setActiveMenu("home");
        setSelectedTopic(null);
      }
      return next;
    });
  };

  const openTopic = (id) => {
    setSelectedTopic(id);
    setActiveMenu("details");
  };

  const goBack = () => {
    setActiveMenu("home");
    setSelectedTopic(null);
  };

  const styles = {
    container: {
      position: "fixed",
      right: "20px",
      bottom: "20px",
      zIndex: 1200,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      fontFamily: '"Poppins", "Segoe UI", sans-serif',
    },

    triggerButton: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "14px 18px",
      border: "none",
      borderRadius: "999px",
      background: "linear-gradient(135deg, #0d6efd, #003c99)",
      color: "#ffffff",
      fontSize: "15px",
      fontWeight: 600,
      cursor: "pointer",
      boxShadow: "0 12px 28px rgba(13, 110, 253, 0.28)",
      transition: "all 0.25s ease",
    },

    chatWindow: {
      width: "360px",
      maxWidth: "calc(100vw - 24px)",
      maxHeight: "78vh",
      marginBottom: "14px",
      borderRadius: "20px",
      overflow: "hidden",
      backgroundColor: "#ffffff",
      boxShadow: "0 20px 50px rgba(15, 23, 42, 0.18)",
      border: "1px solid rgba(15, 23, 42, 0.08)",
      display: "flex",
      flexDirection: "column",
    },

    header: {
      padding: "16px 18px",
      background: "linear-gradient(135deg, #0f172a, #1e3a8a)",
      color: "#ffffff",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "12px",
    },

    headerTextWrap: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    },

    headerTitle: {
      margin: 0,
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: 1.2,
    },

    headerSubtitle: {
      margin: 0,
      fontSize: "12px",
      color: "rgba(255,255,255,0.82)",
      lineHeight: 1.4,
    },

    iconButton: {
      width: "34px",
      height: "34px",
      border: "none",
      borderRadius: "50%",
      backgroundColor: "rgba(255,255,255,0.14)",
      color: "#ffffff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flexShrink: 0,
    },

    body: {
      padding: "16px",
      backgroundColor: "#f8fafc",
      overflowY: "auto",
    },

    introCard: {
      backgroundColor: "#ffffff",
      borderRadius: "14px",
      padding: "14px",
      marginBottom: "14px",
      border: "1px solid #e9eef5",
    },

    introTitle: {
      margin: "0 0 6px 0",
      fontSize: "15px",
      fontWeight: 700,
      color: "#0f172a",
    },

    introText: {
      margin: 0,
      fontSize: "13px",
      color: "#475569",
      lineHeight: 1.6,
    },

    optionsWrap: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },

    optionButton: {
      width: "100%",
      border: "1px solid #dbe4f0",
      backgroundColor: "#ffffff",
      borderRadius: "16px",
      padding: "14px",
      textAlign: "left",
      cursor: "pointer",
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      transition: "all 0.25s ease",
    },

    optionIcon: {
      width: "42px",
      height: "42px",
      borderRadius: "12px",
      background: "linear-gradient(135deg, #e0ecff, #dbeafe)",
      color: "#0d6efd",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "18px",
      flexShrink: 0,
    },

    optionContent: {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      minWidth: 0,
    },

    optionTitle: {
      margin: 0,
      fontSize: "14px",
      fontWeight: 700,
      color: "#0f172a",
    },

    optionDesc: {
      margin: 0,
      fontSize: "12.5px",
      color: "#64748b",
      lineHeight: 1.5,
    },

    sectionCard: {
      backgroundColor: "#ffffff",
      borderRadius: "16px",
      padding: "16px",
      border: "1px solid #e6edf5",
    },

    sectionTitle: {
      margin: "0 0 12px 0",
      fontSize: "16px",
      fontWeight: 700,
      color: "#0f172a",
    },

    infoRow: {
      marginBottom: "10px",
      fontSize: "14px",
      color: "#334155",
      lineHeight: 1.6,
      wordBreak: "break-word",
    },

    label: {
      fontWeight: 700,
      color: "#0f172a",
      marginRight: "6px",
    },

    link: {
      color: "#0d6efd",
      textDecoration: "none",
      fontWeight: 600,
    },

    secondaryLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      marginTop: "8px",
      color: "#0d6efd",
      textDecoration: "none",
      fontWeight: 600,
      fontSize: "14px",
    },

    faqList: {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
    },

    faqItem: {
      padding: "12px",
      borderRadius: "12px",
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
    },

    faqQuestion: {
      margin: "0 0 6px 0",
      fontSize: "13px",
      fontWeight: 700,
      color: "#0f172a",
    },

    faqAnswer: {
      margin: 0,
      fontSize: "13px",
      lineHeight: 1.6,
      color: "#475569",
    },

    footer: {
      padding: "12px 16px",
      borderTop: "1px solid #e8edf4",
      backgroundColor: "#ffffff",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "10px",
      flexWrap: "wrap",
    },

    footerText: {
      margin: 0,
      fontSize: "12px",
      color: "#64748b",
    },

    footerButton: {
      border: "none",
      backgroundColor: "#eaf2ff",
      color: "#0d6efd",
      borderRadius: "10px",
      padding: "8px 12px",
      fontSize: "13px",
      fontWeight: 700,
      cursor: "pointer",
    },
  };

  const renderHome = () => (
    <>
      <div style={styles.introCard}>
        <h6 style={styles.introTitle}>{chatConfig.subtitle}</h6>
        <p style={styles.introText}>
          Choose an option below. This widget is structured so you can later add
          AI chat, ticket creation, FAQs, or service-based flows easily.
        </p>
      </div>

      <div style={styles.optionsWrap}>
        {chatConfig.homeOptions.map((item) => (
          <button
            key={item.id}
            type="button"
            style={styles.optionButton}
            onClick={() => openTopic(item.id)}
          >
            <div style={styles.optionIcon}>
              <i className={item.icon}></i>
            </div>

            <div style={styles.optionContent}>
              <h6 style={styles.optionTitle}>{item.label}</h6>
              <p style={styles.optionDesc}>{item.description}</p>
            </div>
          </button>
        ))}
      </div>
    </>
  );

  const renderDetails = () => {
    if (!selectedItem) return null;

    if (selectedItem.type === "contact") {
      return (
        <div style={styles.sectionCard}>
          <h6 style={styles.sectionTitle}>{selectedItem.data.title}</h6>

          <p style={styles.infoRow}>
            <span style={styles.label}>Email:</span>
            <a
              href={`mailto:${selectedItem.data.email}`}
              style={styles.link}
            >
              {selectedItem.data.email}
            </a>
          </p>

          <p style={styles.infoRow}>
            <span style={styles.label}>Phone:</span>
            <a
              href={`tel:${selectedItem.data.phone.replace(/\s+/g, "")}`}
              style={styles.link}
            >
              {selectedItem.data.phone}
            </a>
          </p>

          <Link to={selectedItem.data.helpLink} style={styles.secondaryLink}>
            Open contact page <i className="bi bi-arrow-right"></i>
          </Link>
        </div>
      );
    }

    if (selectedItem.type === "faq") {
      return (
        <div style={styles.sectionCard}>
          <h6 style={styles.sectionTitle}>{selectedItem.data.title}</h6>
          <div style={styles.faqList}>
            {selectedItem.data.questions.map((item, index) => (
              <div key={index} style={styles.faqItem}>
                <p style={styles.faqQuestion}>{item.question}</p>
                <p style={styles.faqAnswer}>{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (selectedItem.type === "ai") {
      return (
        <div style={styles.sectionCard}>
          <h6 style={styles.sectionTitle}>AI Chat</h6>
          <p style={styles.introText}>
            AI chat can be connected here later using your API endpoint or chat
            service.
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <div style={styles.container}>
      {isOpen && (
        <div style={styles.chatWindow}>
          <div style={styles.header}>
            <div style={styles.headerTextWrap}>
              <h5 style={styles.headerTitle}>{chatConfig.title}</h5>
              <p style={styles.headerSubtitle}>
                Support widget for contact, FAQ, and future AI chat.
              </p>
            </div>

            <button
              type="button"
              style={styles.iconButton}
              onClick={toggleChatbot}
              aria-label="Close chat"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          <div style={styles.body}>
            {activeMenu === "home" ? renderHome() : renderDetails()}
          </div>

          <div style={styles.footer}>
            <p style={styles.footerText}>
              {activeMenu === "home"
                ? "Select a support option"
                : "Go back to choose another option"}
            </p>

            {activeMenu !== "home" && (
              <button type="button" style={styles.footerButton} onClick={goBack}>
                Back
              </button>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        style={styles.triggerButton}
        onClick={toggleChatbot}
        aria-label={isOpen ? "Close chat support" : "Open chat support"}
      >
        <i className={`bi ${isOpen ? "bi-x-lg" : "bi-chat-dots-fill"}`}></i>
        {isOpen ? "Close" : "Chat"}
      </button>
    </div>
  );
};

export default Chatbot;